import { useMemo } from "react";
import { z } from "zod";
import parsePhoneNumber from "libphonenumber-js";

export type FormValues = {
  name: string;
  email: string;
  phone: string;
  resume: File | null;
  agreed: boolean;
};

const validatePhoneNumber = (phone?: string) => {
  if (!phone) return true;
  try {
    const parsed = parsePhoneNumber(phone, "US");
    return parsed?.isValid() ?? false;
  } catch {
    return false;
  }
};

export const contactSchema = z.object({
  name: z.string().nonempty({ message: "Name is required" }),
  email: z
    .string()
    .min(1, { message: "Email is required" })
    .refine((v) => z.email().safeParse(v).success, {
      message: "Invalid email",
    }),
  phone: z
    .string()
    .optional()
    .refine(validatePhoneNumber, { message: "Invalid US phone number" }),
  resume: z.any().refine((v) => v != null, { message: "Resume is required" }),
  agreed: z.literal(true, { message: "Please read and agree to the terms" }),
});

const buildErrors = (result: ReturnType<typeof contactSchema.safeParse>) => {
  if (result.success) return {};
  const out: Record<string, string> = {};
  type Issue = {
    path?: Array<string | number>;
    message: string;
  };
  const issues = (result.error?.issues ?? []) as Issue[];
  for (const e of issues) {
    if (e.path && e.path.length) {
      const key = String(e.path[0]);
      if (!out[key]) out[key] = e.message;
    }
  }
  return out;
};

export const initialValues: FormValues = {
  name: "",
  email: "",
  phone: "",
  resume: null,
  agreed: false,
};

export const validate = (values: FormValues) => {
  const parsed = contactSchema.safeParse(values);
  return buildErrors(parsed);
};

export const performSubmit = async (values: FormValues, position?: string) => {
  const form = new FormData();
  form.append("name", values.name);
  form.append("email", values.email);
  form.append("phone", values.phone);
  if (position) form.append("position", position);
  form.append("agreed", `${values.agreed}`);
  if (values.resume) form.append("resume", values.resume);

  const base = process.env.NEXT_PUBLIC_NEST_API_BASE!;
  const url = `${base.replace(/\/$/, "")}/contact`;

  const resp = await fetch(url, { method: "POST", body: form });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `Server responded ${resp.status}`);
  }
  const json = await resp.json();
  if (json?.redirectPath) {
    return json.redirectPath;
  }
};

export type DefaultHelpersType = {
  setSubmitting: (v: boolean) => void;
  resetForm: (nextState?: unknown) => void;
  setStatus?: (s: string | null) => void;
};
export const defaultHelpers: DefaultHelpersType = {
  setSubmitting: () => {},
  resetForm: () => {},
  setStatus: () => {},
};
export const useAlertState = (
  status: unknown,
  touched: Record<string, unknown>,
  errors: Record<string, unknown>,
) => {
  return useMemo(() => {
    const hasInteracted = touched && Object.keys(touched).length > 0;
    const validationMessage = hasInteracted && Object.keys(errors).length
      ? String(Object.values(errors)[0])
      : null;

    const isReady = hasInteracted && Object.keys(errors).length === 0;

    const alertVariant = status
      ? String(status).startsWith("Failed")
        ? "error"
        : "success"
      : isReady
        ? "success"
        : "error";

    let alertTitle: string | undefined;
    let alertText = "";

    if (status) {
      const statusStr = String(status);
      const parts = statusStr.split(/\s*[—–-]\s*/);
      if (parts.length > 1) {
        alertTitle = parts[0];
        alertText = parts.slice(1).join(" — ");
      } else {
        alertText = statusStr;
      }
    } else if (validationMessage) {
      alertText = validationMessage;
    } else if (isReady) {
      alertTitle = "All fields valid";
      alertText = "Ready to submit.";
    }

    return { alertTitle, alertText, alertVariant } as const;
  }, [status, touched, errors]);
};
